import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Badge,
  VStack,
  HStack,
  Image,
  Link,
  Spinner,
  Center,
  Card,
  CardBody,
  CardHeader,
  Divider,
  IconButton,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  AddIcon,
  AttachmentIcon,
  DeleteIcon,
  EditIcon,
  ExternalLinkIcon,
  InfoOutlineIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import Navbar from './Navbar';
import AuthContext from '../context/AuthContext';
import { API_BASE_URL, PAID_TIER_LABEL } from '../config/adminConfig';

const PAID_CONTENT_ENDPOINT = `${API_BASE_URL}/api/admin/paid-content`;

const emptyFormState = {
  title: '',
  description: '',
  type: 'pdf',
  url: '',
  thumbnail: '',
  status: 'published',
  sortOrder: 10,
};

function BlogsAndVideos() {
  const { token, logout } = useContext(AuthContext);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingContent, setEditingContent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [migrationSeeded, setMigrationSeeded] = useState(false);

  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const toast = useToast();
  const cancelRef = useRef();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const activeColor = '#007AFF';
  const textColor = useColorModeValue('gray.800', 'white');

  const sortedContent = useMemo(
    () =>
      [...content].sort((left, right) => {
        const orderDelta = Number(left.sortOrder || 0) - Number(right.sortOrder || 0);
        if (orderDelta !== 0) {
          return orderDelta;
        }

        return String(left.title || '').localeCompare(String(right.title || ''));
      }),
    [content],
  );

  const getNextSortOrder = () => {
    if (!sortedContent.length) {
      return 10;
    }

    return Math.max(...sortedContent.map(item => Number(item.sortOrder || 0))) + 10;
  };

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetch(PAID_CONTENT_ENDPOINT, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || `HTTP error ${response.status}`);
      }

      setContent(Array.isArray(data?.items) ? data.items : []);
      setMigrationSeeded(Boolean(data?.meta?.seededFromLegacyCatalog));
    } catch (error) {
      console.error('Error fetching paid content:', error);
      toast({
        title: 'Error loading paid content',
        description: error.message || 'Failed to fetch data from server',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setLoading(false);
    }
  }, [logout, toast, token]);

  useEffect(() => {
    if (!token) {
      return;
    }

    fetchContent();
  }, [fetchContent, token]);

  const validateForm = () => {
    if (!editingContent?.title || !editingContent?.url) {
      toast({
        title: 'Validation Error',
        description: 'Title and URL are required fields.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }

    const isValidUrl =
      editingContent.type === 'pdf'
        ? /\.(pdf)($|\?)/i.test(editingContent.url)
        : /\.(mp4|m3u8)($|\?)/i.test(editingContent.url);

    if (!isValidUrl) {
      toast({
        title: 'Invalid URL Format',
        description:
          editingContent.type === 'pdf'
            ? 'Please provide a direct .pdf URL.'
            : 'Please provide a direct .mp4 or .m3u8 URL.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return false;
    }

    return true;
  };

  const handleAddNew = () => {
    setEditingContent({
      ...emptyFormState,
      sortOrder: getNextSortOrder(),
      isNew: true,
    });
    onModalOpen();
  };

  const handleEdit = item => {
    setEditingContent({
      ...item,
      isNew: false,
    });
    onModalOpen();
  };

  const handleDelete = item => {
    setDeleteItem(item);
    onDeleteOpen();
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const method = editingContent.isNew ? 'POST' : 'PATCH';
      const url = editingContent.isNew
        ? PAID_CONTENT_ENDPOINT
        : `${PAID_CONTENT_ENDPOINT}/${encodeURIComponent(editingContent.id)}`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: editingContent.title,
          description: editingContent.description,
          type: editingContent.type,
          url: editingContent.url,
          thumbnail: editingContent.thumbnail,
          status: editingContent.status,
          sortOrder: Number(editingContent.sortOrder || 0),
        }),
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result?.message || result?.errors?.join(', ') || 'Operation failed');
      }

      toast({
        title: editingContent.isNew
          ? 'Content Added Successfully'
          : 'Content Updated Successfully',
        description: editingContent.isNew
          ? 'New content has been added to the premium catalog.'
          : 'The selected content has been updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      await fetchContent();
      onModalClose();
      setEditingContent(null);
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: 'Error Saving Content',
        description: error.message || 'Failed to save content. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleteLoading(true);

      const response = await fetch(
        `${PAID_CONTENT_ENDPOINT}/${encodeURIComponent(deleteItem.id)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 401) {
        logout();
        return;
      }

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result?.message || 'Delete operation failed');
      }

      toast({
        title: 'Content Deleted Successfully',
        description: 'The selected content has been removed from the premium catalog.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });

      await fetchContent();
      onDeleteClose();
      setDeleteItem(null);
    } catch (error) {
      console.error('Error deleting content:', error);
      toast({
        title: 'Error Deleting Content',
        description: error.message || 'Failed to delete content. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const getContentIcon = type => (type === 'pdf' ? <AttachmentIcon /> : <ViewIcon />);
  const getContentTypeColor = type => (type === 'pdf' ? 'blue' : 'green');
  const getStatusColor = status => (status === 'archived' ? 'gray' : 'green');

  return (
    <Box minH="100vh" bg={bgColor}>
      <Navbar />
      <Box maxW="1400px" mx="auto" p={6}>
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Heading size="xl" color={textColor} mb={2}>
              Content Management System
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Manage the unified {PAID_TIER_LABEL} content catalog for the B2C app
            </Text>
          </Box>

          <Card bg={cardBg} border={`1px solid ${borderColor}`} shadow="sm">
            <CardBody>
              <VStack align="start" spacing={3}>
                <HStack spacing={2} color="blue.600">
                  <InfoOutlineIcon />
                  <Text fontWeight="semibold">Backend-owned premium content catalog</Text>
                </HStack>
                <Text color="gray.600">
                  Paid content no longer depends on Google Sheets during normal operation. This
                  screen now writes directly to the B2C backend, so the app and admin portal share
                  the same source of truth.
                </Text>
                {migrationSeeded ? (
                  <Badge colorScheme="orange" variant="subtle">
                    Initial premium catalog seeded from legacy content
                  </Badge>
                ) : (
                  <Badge colorScheme="green" variant="subtle">
                    Backend catalog active
                  </Badge>
                )}
              </VStack>
            </CardBody>
          </Card>

          <Card bg={cardBg} shadow="md" borderRadius="lg" border={`1px solid ${borderColor}`}>
            <CardHeader pb={3}>
              <Flex
                justify="space-between"
                align="center"
                direction={{ base: 'column', md: 'row' }}
                gap={4}
              >
                <HStack spacing={3}>
                  <Heading size="md" color={textColor}>
                    Premium Content Library
                  </Heading>
                  <Badge colorScheme="blue" variant="subtle" px={2} py={1}>
                    {sortedContent.length} items
                  </Badge>
                </HStack>
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  bg={activeColor}
                  _hover={{ bg: '#005FCC', transform: 'translateY(-1px)' }}
                  _active={{ transform: 'translateY(0)' }}
                  shadow="md"
                  onClick={handleAddNew}
                  size={{ base: 'sm', md: 'md' }}
                  w={{ base: 'full', md: 'auto' }}
                >
                  Add New Content
                </Button>
              </Flex>
            </CardHeader>
            <Divider />
            <CardBody pt={4}>
              {loading ? (
                <Center py={10}>
                  <VStack spacing={4}>
                    <Spinner size="lg" color={activeColor} />
                    <Text color="gray.500">Loading premium content...</Text>
                  </VStack>
                </Center>
              ) : sortedContent.length === 0 ? (
                <Center py={10}>
                  <VStack spacing={4}>
                    <Text fontSize="lg" color="gray.500">
                      No content available
                    </Text>
                    <Text fontSize="sm" color="gray.400" textAlign="center">
                      Add some content to start managing the shared paid content catalog.
                    </Text>
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="blue"
                      variant="outline"
                      onClick={handleAddNew}
                    >
                      Add Your First Content
                    </Button>
                  </VStack>
                </Center>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
                    <Thead>
                      <Tr>
                        <Th color={textColor} fontWeight="bold">
                          Order
                        </Th>
                        <Th color={textColor} fontWeight="bold">
                          Preview
                        </Th>
                        <Th color={textColor} fontWeight="bold">
                          Title
                        </Th>
                        <Th color={textColor} fontWeight="bold">
                          Tier
                        </Th>
                        <Th color={textColor} fontWeight="bold">
                          Status
                        </Th>
                        <Th color={textColor} fontWeight="bold">
                          Type
                        </Th>
                        <Th color={textColor} fontWeight="bold" minW="250px">
                          Description
                        </Th>
                        <Th color={textColor} fontWeight="bold">
                          URL
                        </Th>
                        <Th color={textColor} fontWeight="bold">
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sortedContent.map(item => (
                        <Tr key={item.id}>
                          <Td>
                            <Badge colorScheme="gray" variant="outline">
                              {item.sortOrder}
                            </Badge>
                          </Td>
                          <Td>
                            <Box width="60px" height="60px">
                              {item.thumbnail ? (
                                <Image
                                  src={item.thumbnail}
                                  alt={item.title}
                                  width="60px"
                                  height="60px"
                                  objectFit="cover"
                                  borderRadius="md"
                                  fallback={
                                    <Center
                                      width="60px"
                                      height="60px"
                                      bg="gray.100"
                                      borderRadius="md"
                                      border={`1px solid ${borderColor}`}
                                    >
                                      {getContentIcon(item.type)}
                                    </Center>
                                  }
                                />
                              ) : (
                                <Center
                                  width="60px"
                                  height="60px"
                                  bg="gray.100"
                                  borderRadius="md"
                                  border={`1px solid ${borderColor}`}
                                >
                                  {getContentIcon(item.type)}
                                </Center>
                              )}
                            </Box>
                          </Td>
                          <Td maxW="220px">
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="bold" isTruncated title={item.title} color={textColor}>
                                {item.title}
                              </Text>
                              {item.legacySource?.legacyId ? (
                                <Badge colorScheme="orange" variant="subtle">
                                  Legacy ID {item.legacySource.legacyId}
                                </Badge>
                              ) : null}
                            </VStack>
                          </Td>
                          <Td>
                            <Badge colorScheme="blue" variant="subtle">
                              {PAID_TIER_LABEL}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(item.status)} variant="subtle">
                              {item.status}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={getContentTypeColor(item.type)}
                              variant="solid"
                              borderRadius="full"
                            >
                              {item.type === 'pdf' ? 'PDF' : 'VIDEO'}
                            </Badge>
                          </Td>
                          <Td maxW="280px">
                            <Text fontSize="sm" color="gray.600" noOfLines={3}>
                              {item.description || 'No description'}
                            </Text>
                          </Td>
                          <Td maxW="180px">
                            <HStack spacing={2}>
                              <Text fontSize="sm" isTruncated title={item.url} color="gray.500">
                                {item.url}
                              </Text>
                              <Link href={item.url} isExternal>
                                <IconButton
                                  icon={<ExternalLinkIcon />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="blue"
                                  aria-label="Open URL"
                                />
                              </Link>
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Tooltip label="Edit content">
                                <IconButton
                                  icon={<EditIcon />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                  onClick={() => handleEdit(item)}
                                />
                              </Tooltip>
                              <Tooltip label="Delete content">
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={() => handleDelete(item)}
                                  isLoading={isDeleteLoading && deleteItem?.id === item.id}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit}>
          <ModalHeader>
            {editingContent?.isNew ? 'Add New Premium Content' : 'Edit Premium Content'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={editingContent?.title || ''}
                  onChange={event =>
                    setEditingContent(current => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                  placeholder="2026 Outlook: What Matters for Retail Investors"
                />
              </FormControl>

              <HStack spacing={4} w="full" align="start">
                <FormControl isRequired>
                  <FormLabel>Type</FormLabel>
                  <Select
                    value={editingContent?.type || 'pdf'}
                    onChange={event =>
                      setEditingContent(current => ({
                        ...current,
                        type: event.target.value,
                      }))
                    }
                  >
                    <option value="pdf">PDF Document</option>
                    <option value="video">Video</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={editingContent?.status || 'published'}
                    onChange={event =>
                      setEditingContent(current => ({
                        ...current,
                        status: event.target.value,
                      }))
                    }
                  >
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Sort Order</FormLabel>
                  <Input
                    type="number"
                    value={editingContent?.sortOrder ?? 10}
                    onChange={event =>
                      setEditingContent(current => ({
                        ...current,
                        sortOrder: Number(event.target.value || 0),
                      }))
                    }
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={editingContent?.description || ''}
                  onChange={event =>
                    setEditingContent(current => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  placeholder="Why this content matters and how it supports better decisions."
                  rows={4}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  {editingContent?.type === 'pdf' ? 'PDF URL' : 'Video URL'}
                </FormLabel>
                <Input
                  value={editingContent?.url || ''}
                  onChange={event =>
                    setEditingContent(current => ({
                      ...current,
                      url: event.target.value,
                    }))
                  }
                  placeholder={
                    editingContent?.type === 'pdf'
                      ? 'https://example.com/document.pdf'
                      : 'https://example.com/video.mp4'
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Thumbnail URL</FormLabel>
                <Input
                  value={editingContent?.thumbnail || ''}
                  onChange={event =>
                    setEditingContent(current => ({
                      ...current,
                      thumbnail: event.target.value,
                    }))
                  }
                  placeholder="https://example.com/thumbnail.jpg"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={onModalClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" type="submit" isLoading={isSubmitting}>
              {editingContent?.isNew ? 'Create Content' : 'Save Changes'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Premium Content
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{deleteItem?.title}"? This action cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3} isLoading={isDeleteLoading}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default BlogsAndVideos;
